<template>
  <FormGroup :label="processedLabel">
    <template v-if="integratedPartner">
      <InputText v-if="isUnstructured" v-model="model" size="large" class="w-full" :aria-label="ariaLabel" />
      <Select
        v-else
        ref="select"
        v-model="model"
        filter
        filter-placeholder="Search..."
        :options="fields"
        :loading="isLoading"
        option-label="label"
        option-group-label="label"
        option-group-children="items"
        option-value="value"
        :show-clear="canClear"
        class="w-full"
        :aria-label="ariaLabel"
        :placeholder="placeholder"
        :disabled="isLoading">
        <template v-if="canCreateCustomField" #footer>
          <div class="border-t border-line p-1">
            <button
              type="button"
              data-create-field
              class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-primary hover:bg-tint disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="fieldsUnavailable"
              @click="openCreateDialog">
              <PlusIcon class="size-4" aria-hidden="true" />
              New {{ integratedPartner.label }} {{ fieldNoun.toLowerCase() }}
            </button>
          </div>
        </template>
      </Select>
      <p v-if="fieldsUnavailable" role="status" class="mt-1.5 text-xs text-warning">
        <template v-if="integratedPartner.needsReconnect">
          Reconnect {{ integratedPartner.label }} to load fields.
        </template>
        <template v-else> Couldn't load {{ integratedPartner.label }} fields. </template>
        Your saved field is unchanged.
        <RouterLink
          :to="{ name: ROUTES.INTEGRATION, params: { integration: integratedPartner.name } }"
          class="font-semibold underline"
          >Manage connection</RouterLink
        >
      </p>
    </template>
    <template v-else>
      <div class="rounded-sm bg-white px-3 py-2 text-brand-text ring-1 ring-3 ring-surface-900/10">
        No integration available
      </div>
    </template>
  </FormGroup>

  <CreateCustomFieldDialog v-model="showCreateDialog" @created="handleFieldCreated" />
</template>

<script setup lang="ts">
import { useIntegrationDataStore } from '@dashboard/modules/integration/stores/integrationDataStore'
import CreateCustomFieldDialog from './CreateCustomFieldDialog.vue'
import FormGroup from '@dashboard/components/common/FormGroup.vue'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { PlusIcon } from '@heroicons/vue/24/outline'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import { computed, ref, watchEffect } from 'vue'
import { RouterLink } from 'vue-router'
import { ROUTES } from '@dashboard/router/routeNames'

const props = defineProps({
  label: { type: [String, Boolean], default: '$FIELD' },
  ariaLabel: { type: String },
  prefix: { type: Boolean, default: true },
  includeNative: { type: Boolean, default: true },
  placeholder: { type: String },
  canClear: { type: Boolean, default: true },
  /** Offers the create-field footer when the platform supports it; conditions pick existing fields only. */
  creatable: { type: Boolean, default: true }
})
const model = defineModel<string | null>()

const showCreateDialog = ref(false)
const select = ref<{ hide: () => void } | null>(null)
function openCreateDialog() {
  select.value?.hide()
  showCreateDialog.value = true
}

const integrationStore = useIntegrationStore()
const integratedPartner = computed(() => integrationStore.integratedPartner)
const integrationDataStore = useIntegrationDataStore()

const isUnstructured = computed(() => {
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  return customField && typeof customField === 'object' && 'unstructured' in customField
    ? customField.unstructured
    : false
})

const canCreateCustomField = computed(() => {
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  return (
    props.creatable &&
    customField &&
    typeof customField === 'object' &&
    customField.create === true &&
    !customField.unstructured // If unstructured is not set, it defaults to false (structured)
  )
})

const nativeFieldsData = ref<{ id: string | number; label: string }[]>([])
const customFieldsData = ref<{ id: string | number; label: string }[]>([])
const isLoading = ref(false)
const loadFailed = ref(false)
const fieldsUnavailable = computed(() => !!integratedPartner.value?.needsReconnect || loadFailed.value)

// Preserve the saved destination even when its provider cannot return field metadata.
watchEffect(async (onCleanup) => {
  const partner = integratedPartner.value
  const includeNative = props.includeNative
  let active = true
  onCleanup(() => {
    active = false
  })
  nativeFieldsData.value = []
  customFieldsData.value = []
  loadFailed.value = false
  isLoading.value = false
  if (!partner || partner.needsReconnect || isUnstructured.value) {
    return
  }

  isLoading.value = true
  try {
    const [nativeFields, customFields] = await Promise.all([
      includeNative ? integrationDataStore.getDataResult('partner', 'native_fields') : { data: [], failed: false },
      integrationDataStore.getCustomFieldsResult()
    ])
    if (active) {
      nativeFieldsData.value = nativeFields.data
      customFieldsData.value = customFields.data
      loadFailed.value = nativeFields.failed || customFields.failed
    }
  } catch (error) {
    if (active) {
      console.error('Error fetching integration fields:', error)
      loadFailed.value = true
    }
  } finally {
    if (active) {
      isLoading.value = false
    }
  }
})

const availableNativeFields = computed(() => {
  if (!props.includeNative) {
    return []
  }
  return nativeFieldsData.value.map((field) => ({
    label: field.label,
    value: props.prefix ? `native*${field.id}` : String(field.id)
  }))
})

const availableCustomFields = computed(() =>
  customFieldsData.value.map((field) => ({
    label: field.label,
    value: props.prefix ? `custom*${field.id}` : String(field.id)
  }))
)

const fields = computed(() => {
  const groups = [
    ...(props.includeNative ? [{ label: 'Native Fields', items: availableNativeFields.value }] : []),
    { label: 'Custom Fields', items: availableCustomFields.value }
  ]
  const savedValue = model.value
  if (savedValue && !groups.some((group) => group.items.some((field) => field.value === savedValue))) {
    const label = props.prefix ? savedValue.replace(/^(native|custom)\*/, '') : savedValue
    groups.push({ label: 'Saved field', items: [{ label, value: savedValue }] })
  }
  return groups
})

const fieldNoun = computed(() => {
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  return customField && typeof customField === 'object' && 'label' in customField ? customField.label : 'Field'
})

const processedLabel = computed(() =>
  typeof props.label === 'string' ? props.label.replace('$FIELD', fieldNoun.value) : props.label
)

const handleFieldCreated = async (fieldId: string) => {
  // Set the model value to the new field's ID
  model.value = props.prefix ? `custom*${fieldId}` : fieldId

  const updatedFields = await integrationDataStore.getCustomFieldsResult()
  customFieldsData.value = updatedFields.data
  loadFailed.value = updatedFields.failed
}
</script>
